
import { defineComponent } from 'vue'
import { Card, Form, Input, Password, PrimaryButton } from '@design-system/ui'
import Link from '../components/Link.vue'
import Toast from '../components/Toast.vue'
import Validator from '../components/Validator.vue'
import api from '../services/api'
import SessionService from '../SessionService'
import { Role } from '@/enums/Roles.enum'

export default defineComponent({
  name: 'Auth',
  components: {
    Card,
    Form,
    Input,
    Password,
    PrimaryButton,
    Link,
    Validator,
    Toast
  },
  emits: [
    'authenticate'
  ],
  data: () => {
    return {
      state: 'authenticating',
      recoverStatus: 'identifying',
      user: '',
      newUser: '',
      newUserMessage: '',
      userMessage: '',
      password: '',
      passwordMessage: '',
      email: '',
      emailMessage: '',
      cnpj: '',
      cnpjMessage: '',
      newPassword: '',
      repeatNewPassword: '',
      newPasswordMessage: '',
      repeatNewPasswordMessage: '',
      userName: '',
      code: '',
      validating: false,
      isNew: false,
      hidden: true,
      message: '',
      success: false,
      timeSeen: 20000,
      userData: {},
      hasUppercase: ' ',
      hasLowercase: ' ',
      hasNumber: ' ',
      hasSpecialCharacter: ' ',
      hasMinLength: ' '
    }
  },
  methods: {
    authenticate () {
      if (!this.user || this.user === '') {
        this.userMessage = 'Informe um usuário'
      } else {
        this.userMessage = ''
      }
      if (!this.password || this.password === '') {
        this.passwordMessage = 'Informe uma senha'
      } else {
        this.passwordMessage = ''
      }
      if (this.userMessage === '' && this.passwordMessage === '') {
        api.post('/accounts/login', {
          username: this.user,
          password: this.password
        }).then(async (response) => {
          this.state = 'loading'

          const token = response.data.accessToken

          localStorage.setItem('token', token)

          const session = new SessionService()
          const data = session.data(token)

          this.userData = data.systems[0]
          const softwareHouse = await session.softwareHouseData(token, data.systems[0].cnpj)

          this.userData.nomeFantasia = softwareHouse.person?.juridicPerson?.tradingName

          try {
            const image = await session.getImageProfile(token, this.userData.cnpj, data.userId)
            this.userData.avatarImage = image
          } catch (error) {
            console.log(error)
          }

          this.$store.commit('populateSoftwareHouse', softwareHouse)
          this.$store.commit('populateUser', data.systems[0])

          if (this.userData.type === Role.APROVACAO) {
            this.$router.push('/aprovacoes')
          } else {
            this.$router.push('/painel')
          }
          this.$emit('authenticate')
        }).catch(() => {
          this.hidden = false
          this.message = 'Credenciais inválidas.'
          setTimeout(() => { this.hidden = true }, this.timeSeen)
          this.state = 'authenticating'
        })
      }
    },

    recover (isNew : boolean) {
      this.isNew = isNew
      this.state = 'recovering'
      this.recoverStatus = 'identifying'
    },

    identify (isNew: boolean) {
      this.userName = ''
      this.userType = ''
      this.userIsApp = ''
      if (isNew) {
        if (!this.email || this.email === '') {
          this.emailMessage = 'Informe um E-mail'
        } else {
          this.emailMessage = ''
        }
        if (!this.cnpj || this.cnpj === '') {
          this.cnpjMessage = 'Informe um CNPJ'
        } else {
          this.cnpjMessage = ''
        }
        if (this.cnpjMessage === '' && this.emailMessage === '') {
          api.post('/accounts/first-access', {
            email: this.email,
            cnpj: this.cnpj.replaceAll('-', '').replaceAll('.', '').replaceAll('/', '')
          }).then((response) => {
            this.recoverStatus = 'validating'
            this.userName = response.data.nome
            this.userType = response.data.tipo
            this.userIsApp = response.data.isApp
          }).catch((error) => {
            this.hidden = false
            this.message = error.response.data.error_messages[0] ? error.response.data.error_messages[0] : 'Erro ao realizar o primeiro acesso.'
            setTimeout(() => { this.hidden = true }, this.timeSeen)
          })
        }
      } else {
        if (!this.user || this.user === '') {
          this.userMessage = 'Informe um usuário'
        } else {
          this.userMessage = ''
        }
        if (this.userMessage === '') {
          api.post('/accounts/password', {
            username: this.user
          }).then(() => {
            this.recoverStatus = 'validating'
          }).catch(() => {
            this.hidden = false
            this.message = 'Pessoa juridica não encontrada.'
            setTimeout(() => { this.hidden = true }, this.timeSeen)
          })
        }
      }
    },

    validate (value: string) {
      api.post('/accounts/validate', {
        email: this.isNew ? this.email : this.user,
        codigo: value,
        type: this.isNew ? 'first_access' : 'forgot_password'
      }).then((response) => {
        if (response.data.valid) {
          this.recoverStatus = 'redefining'
        } else {
          this.hidden = false
          this.message = 'Código inválido.'
          setTimeout(() => { this.hidden = true }, this.timeSeen)
        }
      }).catch(() => {
        this.hidden = false
        this.message = 'Código inválido.'
        setTimeout(() => { this.hidden = true }, this.timeSeen)
      })
    },

    redefine () {
      this.hasUppercase = /[A-Z]/.test(this.newPassword)
      this.hasLowercase = /[a-z]/.test(this.newPassword)
      this.hasNumber = /\d/.test(this.newPassword)
      this.hasSpecialCharacter = /[!@#$%^&*()\-_=+]/.test(this.newPassword)
      this.hasMinLength = this.newPassword.length >= 8

      this.isPasswordValid = (
        this.newPassword.length >= 8 &&
        this.hasUppercase &&
        this.hasLowercase &&
        this.hasNumber &&
        this.hasSpecialCharacter
      )

      if (!this.newPassword || this.newPassword === '' || !this.isPasswordValid) {
        this.newPasswordMessage = 'Informe uma senha'
      } else {
        this.newPasswordMessage = ''
      }
      if (!this.repeatNewPassword || this.repeatNewPassword === '' || !this.isPasswordValid) {
        this.repeatNewPasswordMessage = 'Informe uma senha'
      } else {
        if (this.newPassword !== this.repeatNewPassword) {
          this.repeatNewPasswordMessage = 'A senhas informadas devem ser iguais'
        } else {
          this.repeatNewPasswordMessage = ''
        }
      }
      if (this.newUserMessage === '' && this.newPasswordMessage === '' && this.repeatNewPasswordMessage === '') {
        if (this.isNew) {
          api.post('/accounts/', {
            name: this.userName,
            username: this.email,
            password: this.newPassword,
            cnpj: this.cnpj.replaceAll('-', '').replaceAll('.', '').replaceAll('/', ''),
            system: 'tecnopay',
            type: this.userType,
            isApp: this.userIsApp
          }).then(() => {
            this.state = 'authenticating'
            this.$router.push('/')
          }).catch((error) => {
            this.hidden = false
            this.message = error.response.data.error_messages[0] ? error.response.data.error_messages[0] : 'Erro ao cadastrar as credenciais.'
            setTimeout(() => { this.hidden = true }, this.timeSeen)
          })
        } else {
          api.patch('/accounts/password', {
            email: this.user,
            username: this.user,
            password: this.newPassword
          }).then(() => {
            this.hidden = false
            this.success = true
            this.message = 'Sua senha foi alterada com sucesso, tente logar novamente'
            setTimeout(() => { this.hidden = true }, this.timeSeen)
            this.back()
          }).catch((error) => {
            this.hidden = false
            this.message = error.response.data.error_messages[0] ? error.response.data.error_messages[0] : 'Erro ao cadastrar as credenciais.'
            setTimeout(() => { this.hidden = true }, this.timeSeen)
          })
        }
      }
    },

    back () {
      this.state = 'authenticating'
      this.clean()
    },

    hide () {
      this.hidden = true
    },

    clean () {
      this.user = ''
      this.newUser = ''
      this.newUserMessage = ''
      this.userMessage = ''
      this.password = ''
      this.passwordMessage = ''
      this.email = ''
      this.emailMessage = ''
      this.newPassword = ''
      this.repeatNewPassword = ''
      this.newPasswordMessage = ''
      this.repeatNewPasswordMessage = ''
    }
  }
})
